import { Link } from "react-router";
import { cn } from "@veo/ui";
import { formatDate } from "../lib/format";
import { ResponsiveImage } from "./ResponsiveImage";

export interface PostSummary {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  publishedAt: string | null;
  readingMinutes: number | null;
  coverImage: {
    url: string;
    srcSet: string;
    width: number | null;
    height: number | null;
    alt: string;
    placeholder: string | null;
  } | null;
  category: { slug: string; name: string } | null;
  author: { name: string; slug: string } | null;
}

/**
 * One article in a listing.
 *
 * The whole card is not a link. A single link wrapping the image, the title and
 * the metadata gives a screen reader one enormous link name; instead the title
 * is the link and a `::after` overlay makes the rest of the card clickable, so
 * the pointer target is large while the accessible name stays "the title".
 */
export function ArticleCard({
  post,
  showExcerpt = true,
  showAuthor = true,
  showDate = true,
  showReadingTime = true,
  onDark = false,
  sizes = "(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw",
  priority = false,
}: {
  post: PostSummary;
  showExcerpt?: boolean;
  showAuthor?: boolean;
  showDate?: boolean;
  showReadingTime?: boolean;
  onDark?: boolean;
  sizes?: string;
  priority?: boolean;
}) {
  const meta = [
    showDate && post.publishedAt ? formatDate(post.publishedAt) : null,
    showReadingTime && post.readingMinutes ? `${post.readingMinutes} min read` : null,
    showAuthor && post.author ? post.author.name : null,
  ].filter(Boolean);

  return (
    <article
      className={cn(
        // The card carries its own surface rather than sitting flat on the
        // section background, matching the treatment of every other card.
        "group relative flex flex-col overflow-hidden rounded-lg",
        onDark ? "bg-navy-800" : "bg-white shadow-card",
      )}
    >
      <div
        className={cn("aspect-[16/10] overflow-hidden", onDark ? "bg-navy-700" : "bg-ink-100")}
      >
        {post.coverImage && (
          <ResponsiveImage
            image={post.coverImage}
            sizes={sizes}
            priority={priority}
            className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        )}
      </div>

      <div className="flex flex-1 flex-col p-6">
        {post.category && (
          <p
            className={cn(
              "text-xs font-semibold uppercase tracking-wider",
              onDark ? "text-gold-300" : "text-gold-700",
            )}
          >
            {post.category.name}
          </p>
        )}

        <h3
          className={cn(
            "mt-2 font-display text-xl leading-snug",
            onDark ? "text-cream-50" : "text-navy-900",
          )}
        >
          <Link
            to={`/insights/${post.slug}`}
            className="after:absolute after:inset-0 hover:underline focus-visible:underline"
          >
            {post.title}
          </Link>
        </h3>

        {showExcerpt && post.excerpt && (
          <p
            className={cn(
              "mt-3 line-clamp-3 leading-relaxed",
              onDark ? "text-navy-200" : "text-ink-600",
            )}
          >
            {post.excerpt}
          </p>
        )}

        {meta.length > 0 && (
          <p className={cn("mt-4 text-sm", onDark ? "text-navy-300" : "text-ink-500")}>
            {meta.map((entry, index) => (
              <span key={String(entry)}>
                {index > 0 && <span aria-hidden> · </span>}
                {index === 0 && showDate && post.publishedAt ? (
                  <time dateTime={post.publishedAt}>{entry}</time>
                ) : (
                  entry
                )}
              </span>
            ))}
          </p>
        )}
      </div>
    </article>
  );
}
